/**
 * 固件管理 API 服务
 * 连接后端固件管理系统与前端升级功能
 */

export interface FirmwareUploadRequest {
  file: File
  deviceName: string
  fileName: string
  uploader: string
  password: string
  remark?: string
}

export interface FirmwareListItem {
  id: number
  deviceName: string
  fileName: string
  filePath?: string
  fileSize: number
  uploadTime: string
  uploader: string
  remark?: string
}

export interface FirmwareApiResponse<T = any> {
  code: number
  message: string
  data: T
}

const API_BASE = 'https://www.aiiifly.com/api/firmware'

/**
 * 获取固件列表
 */
export async function getFirmwareList(): Promise<FirmwareListItem[]> {
  try {
    const response = await fetch(`${API_BASE}/list`)
    if (!response.ok) throw new Error('Network error')
    const res: FirmwareApiResponse<FirmwareListItem[]> = await response.json()
    if (res.code === 1) {
      return res.data || []
    }
    throw new Error(res.message || 'Failed to fetch firmware list')
  } catch (error) {
    console.warn('Failed to fetch firmware list from backend, will use mock data')
    return []
  }
}

/**
 * 上传固件
 */
export async function uploadFirmware(request: FirmwareUploadRequest): Promise<void> {
  const formData = new FormData()
  formData.append('file', request.file)
  formData.append('deviceName', request.deviceName)
  formData.append('fileName', request.fileName)
  formData.append('uploader', request.uploader)
  formData.append('password', request.password)
  if (request.remark) {
    formData.append('remark', request.remark)
  }

  const response = await fetch(`${API_BASE}/upload`, {
    method: 'POST',
    body: formData,
  })

  const res: FirmwareApiResponse = await response.json()
  if (res.code !== 1) {
    throw new Error(res.message || 'Upload failed')
  }
}

/**
 * 删除固件
 */
export async function deleteFirmware(id: number, password: string): Promise<void> {
  const formData = new FormData()
  formData.append('id', String(id))
  formData.append('password', password)

  const response = await fetch(`${API_BASE}/delete`, {
    method: 'POST',
    body: formData,
  })

  const res: FirmwareApiResponse = await response.json()
  if (res.code !== 1) {
    throw new Error(res.message || 'Delete failed')
  }
}

/**
 * 转换后端数据到升级系统格式
 */
export function convertToUpgradeFormat(items: FirmwareListItem[]) {
  return items.map(item => {
    // 从文件名解析版本号和颜色
    const parsed = parseFirmwareFileName(item.fileName)

    return {
      version: parsed.version,
      fileName: item.fileName,
      color: parsed.color as 'black' | 'white' | undefined,
      factoryOnly: false,
      description: item.remark || 'Firmware update',
      update_date: formatUploadTime(item.uploadTime),
      downloadUrl: buildDownloadUrl(item.deviceName, item.fileName),
      fileSize: item.fileSize,
      uploader: item.uploader,
    }
  })
}

/**
 * 从文件名解析版本号和颜色
 * 例如: G1_Headset_Black_260805_V1.1.10.up
 */
function parseFirmwareFileName(fileName: string): { version: string; color: 'black' | 'white' | '' } {
  const name = String(fileName || '').trim()

  // 提取版本号 (V1.1.10)
  const versionMatch = name.match(/[vV](\d+\.\d+\.\d+)/i)
  const version = versionMatch ? `V${versionMatch[1]}` : ''

  // 提取颜色
  let color: 'black' | 'white' | '' = ''
  const lowerName = name.toLowerCase()
  if (lowerName.includes('black')) {
    color = 'black'
  } else if (lowerName.includes('white')) {
    color = 'white'
  }

  return { version, color }
}

/**
 * 格式化上传时间
 */
function formatUploadTime(uploadTime: string): string {
  try {
    const date = new Date(uploadTime)
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const hour = String(date.getHours()).padStart(2, '0')
    const minute = String(date.getMinutes()).padStart(2, '0')
    return `${year}.${month}.${day} ${hour}:${minute}`
  } catch {
    return uploadTime
  }
}

/**
 * 构建下载 URL (路径固定，前端拼接)
 */
function buildDownloadUrl(deviceName: string, fileName: string): string {
  const base = import.meta.env.BASE_URL || '/'
  // 固定路径格式：/upgrading/{deviceName}/{fileName}
  return `${base}upgrading/${deviceName}/${fileName}`
}

/**
 * 按设备类型分组固件列表
 */
export function groupFirmwareByDevice(items: FirmwareListItem[]) {
  const groups: Record<string, FirmwareListItem[]> = {}

  items.forEach(item => {
    if (!groups[item.deviceName]) {
      groups[item.deviceName] = []
    }
    groups[item.deviceName].push(item)
  })

  return groups
}
